import { AdminLayout } from "@/components/admin/AdminLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { RefreshCw, RotateCcw, Save } from "lucide-react";
import { usePipelineTolerances } from "@/hooks/usePipelineTolerances";

export default function PipelineTolerances() {
  const {
    loading,
    saving,
    error,
    flagEnabled,
    draftConfig,
    isDirty,
    updateField,
    resetDraft,
    save,
    refresh,
  } = usePipelineTolerances();

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-4 max-w-5xl">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-xl font-semibold">Pipeline Tolerances</h1>
            <p className="text-sm text-muted-foreground">
              Configure tolerance policy baselines for capture/manifest and downstream S1 gate guardrails.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={flagEnabled ? "default" : "secondary"}>
              {flagEnabled ? "Enforcement enabled" : "Enforcement disabled (flag off)"}
            </Badge>
            <Button variant="outline" size="sm" onClick={refresh}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" disabled={!isDirty || saving} onClick={resetDraft}>
              <RotateCcw className="h-4 w-4 mr-1" />
              Discard
            </Button>
            <Button size="sm" disabled={!isDirty || saving} onClick={save}>
              <Save className="h-4 w-4 mr-1" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        {error ? (
          <Card className="border-destructive/50">
            <CardContent className="pt-6 text-sm text-destructive">{error}</CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Capture Tolerance</CardTitle>
            <CardDescription>Controls how many missing manifest objects can be tolerated before hard fail.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="capture_missing_pages_pct">Missing Pages % (0-1)</Label>
              <Input
                id="capture_missing_pages_pct"
                type="number"
                min={0}
                max={1}
                step={0.01}
                value={draftConfig.capture_missing_pages_pct}
                onChange={(e) => updateField("capture_missing_pages_pct", Number(e.target.value || 0))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="capture_missing_pages_abs">Missing Pages Abs</Label>
              <Input
                id="capture_missing_pages_abs"
                type="number"
                min={0}
                step={1}
                value={draftConfig.capture_missing_pages_abs}
                onChange={(e) => updateField("capture_missing_pages_abs", Number(e.target.value || 0))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="capture_timeout_treat_as_pending">Timeout Handling</Label>
              <div className="flex items-center justify-between rounded-md border px-3 py-2 h-10">
                <span className="text-sm">Treat timeout as pending</span>
                <Switch
                  id="capture_timeout_treat_as_pending"
                  checked={draftConfig.capture_timeout_treat_as_pending}
                  onCheckedChange={(checked) => updateField("capture_timeout_treat_as_pending", checked)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Manifest Verification</CardTitle>
            <CardDescription>Stability and verification retry settings for capture manifest finalization.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="manifest_recheck_attempts">Recheck Attempts</Label>
              <Input
                id="manifest_recheck_attempts"
                type="number"
                min={0}
                step={1}
                value={draftConfig.manifest_recheck_attempts}
                onChange={(e) => updateField("manifest_recheck_attempts", Number(e.target.value || 0))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="manifest_recheck_delay_ms">Recheck Delay (ms)</Label>
              <Input
                id="manifest_recheck_delay_ms"
                type="number"
                min={0}
                step={100}
                value={draftConfig.manifest_recheck_delay_ms}
                onChange={(e) => updateField("manifest_recheck_delay_ms", Number(e.target.value || 0))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ocr_scope_unstable_grace_sec">OCR Scope Unstable Grace (sec)</Label>
              <Input
                id="ocr_scope_unstable_grace_sec"
                type="number"
                min={0}
                step={1}
                value={draftConfig.ocr_scope_unstable_grace_sec}
                onChange={(e) => updateField("ocr_scope_unstable_grace_sec", Number(e.target.value || 0))}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>OCR / Signals Guardrails</CardTitle>
            <CardDescription>Future-ready tolerance controls for fan-out and blocked retry cadence.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ocr_fanout_rate_limit_tolerance_pct">OCR Fan-out Rate Limit Tolerance % (0-1)</Label>
              <Input
                id="ocr_fanout_rate_limit_tolerance_pct"
                type="number"
                min={0}
                max={1}
                step={0.01}
                value={draftConfig.ocr_fanout_rate_limit_tolerance_pct}
                onChange={(e) => updateField("ocr_fanout_rate_limit_tolerance_pct", Number(e.target.value || 0))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signals_retry_after_ms">Signals Retry After (ms)</Label>
              <Input
                id="signals_retry_after_ms"
                type="number"
                min={1000}
                step={1000}
                value={draftConfig.signals_retry_after_ms}
                onChange={(e) => updateField("signals_retry_after_ms", Number(e.target.value || 0))}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
